import { Select, type SelectItem } from '@aecom-ai-engineering/react-components';
import { type FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { fetchMessageTemplates } from '@/locales/message-templates';
import { notifyFacadeLanguageChange } from '@/utils/language';

/** Same order as the page header's language menu, which this replaces. */
const LANGUAGE_CODES = ['en', 'nb', 'nn', 'de', 'es', 'ca', 'fr', 'sv', 'ja', 'vi', 'zh'] as const;

/** The side nav's language switcher. */
export const LanguageSelect: FC = () => {
  const { t, i18n } = useTranslation();

  const items: SelectItem[] = useMemo(
    () => LANGUAGE_CODES.map((code) => ({ label: t(`language.${code}`), value: code })),
    [t],
  );

  /**
   * `i18n.language` can carry a region (en-US, zh-CN), which matches no item
   * value and would leave the trigger on its placeholder. An unrecognised code
   * yields no selection rather than a wrong one.
   */
  const value = useMemo(() => {
    const [base] = (i18n.resolvedLanguage ?? i18n.language ?? '').split('-');
    return (LANGUAGE_CODES as readonly string[]).includes(base) ? [base] : [];
  }, [i18n.language, i18n.resolvedLanguage]);

  const handleChange = useCallback(
    async (next: string[]) => {
      const [code] = next;
      if (!code) return;

      await fetchMessageTemplates(code, 'translation');
      i18n.changeLanguage(code);
      notifyFacadeLanguageChange(code);
    },
    [i18n],
  );

  /*
   * `label`, not `menuLabel`: label is the trigger's accessible name and
   * defaults to a hardcoded English "Select option". placeholder defaults to
   * English too, and shows whenever value is empty.
   */
  return (
    <Select
      items={items}
      label={t('language.select')}
      // Collapsed, the menu would otherwise inherit the trigger's 36px. Expanded
      // this is the 208px it already had, so it is safe unconditionally.
      menuWidth="default"
      onValueChange={({ value: next }) => void handleChange(next)}
      placeholder={t('language.select')}
      value={value}
    />
  );
};
